import React, { useMemo } from "react";
import {
  isDispensaryCurrentlyOpen,
  getNextOpenSchedule,
  getClosingTime,
} from "@leafly-com/web-utils";
import classnames from "classnames";
import { format } from "date-fns-tz";
import { useSelector } from "react-redux";
import { getCurrentDispensary } from "redux/selectors/dispensary";
import { formatTime } from "utils/dateDisplay";
import { Dispensary } from "custom-types/Dispensary";
import {
  getOpeningHours,
  getWeeklySchedule,
} from "redux/selectors/dispensaryHelper";

const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const shortenedTimeZone = {
  ADT: "AT",
  AST: "AT",
  CDT: "CT",
  CST: "CT",
  EDT: "ET",
  EST: "ET",
  MDT: "MT",
  MST: "MT",
  PDT: "PT",
  PST: "PT",
} as any;

type OpenStatusDispensaryProps = {
  className?: string;
  dispensary?: Dispensary | null;
};
export const OpenStatusDispensary: React.FC<OpenStatusDispensaryProps> = ({
  className,
  dispensary,
}) => {
  const { hasHours, isOpenToday } = useMemo(() => getOpeningHours(dispensary), [
    dispensary,
  ]);
  const weeklySchedule = useMemo(() => getWeeklySchedule(dispensary), [
    dispensary,
  ]);
  const { timeZone } = dispensary || {};

  if (!weeklySchedule || !hasHours) {
    return (
      <div className={classnames("text-xs", className)}>
        <span className="font-bold">Hours unavailable</span>
      </div>
    );
  }

  const isOpenNow = isDispensaryCurrentlyOpen(weeklySchedule, timeZone);

  const weekdayName = (daysFromNow: number) => {
    const dayIndexNow = new Date().getDay();
    return weekDays[(dayIndexNow + daysFromNow) % 7];
  };

  let openStatusLabel = "";
  let openStatusDetails = "";
  if (isOpenNow) {
    openStatusLabel = "Open";
    const closingTime = getClosingTime(weeklySchedule, timeZone);
    openStatusDetails = `until ${formatTime(closingTime, {
      onlyHours: true,
      isUtc: true,
    })}`;
  } else {
    const nextOpenSchedule = getNextOpenSchedule(weeklySchedule, timeZone);
    // Doesn't open again for a couple days
    if (nextOpenSchedule && (nextOpenSchedule.daysAhead > 1 || !isOpenToday)) {
      const nextOpenWeekday = weekdayName(nextOpenSchedule.daysAhead);
      openStatusLabel = `Opens ${nextOpenWeekday}`;
      openStatusDetails = `at ${formatTime(nextOpenSchedule.open, {
        onlyHours: true,
        isUtc: true,
      })}`;
    } else if (nextOpenSchedule) {
      openStatusLabel = "Closed";
      openStatusDetails = `until ${formatTime(nextOpenSchedule.open, {
        onlyHours: true,
        isUtc: true,
      })}`;
    } else {
      openStatusLabel = "Closed";
    }
  }

  if (
    timeZone &&
    Intl.DateTimeFormat().resolvedOptions().timeZone !== timeZone
  ) {
    const timeZoneAbbr = format(new Date(), "z", {
      timeZone: timeZone,
    });
    const shortened = shortenedTimeZone[timeZoneAbbr] || timeZoneAbbr;
    openStatusDetails += ` (${shortened})`;
  }

  return (
    <div className={classnames("text-xs", className)}>
      <span className="font-bold">{openStatusLabel}&nbsp;</span>
      {openStatusDetails && <span className="mr-xs">{openStatusDetails}</span>}
    </div>
  );
};

const OpenStatus = ({ className }: { className?: string }) => {
  const dispensary = useSelector(getCurrentDispensary);

  return <OpenStatusDispensary className={className} dispensary={dispensary} />;
};

export default OpenStatus;
